/**
 * Legend API Service - "铸造传奇" 社区分享系统
 *
 * 核心功能：
 * - 传奇作品管理（铸造、查看、更新）
 * - 致敬系统（点赞升级版）
 * - 骑士档案（用户等级、徽章）
 * - 名人堂（排行榜、精选）
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

// Token management (复用 api.js 的 token)
function getToken() {
    return localStorage.getItem('accessToken');
}

// Fetch wrapper with auth
async function fetchWithAuth(url, options = {}) {
    const token = getToken();
    const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${url}`, {
        ...options,
        headers,
    });

    return response;
}

// =============================================
// 传奇作品 API (Legends)
// =============================================

/**
 * 铸造传奇 - 发布配置为传奇作品
 * @param {Object} data - { configuration_id, title, description }
 */
export async function forgeLegend(data) {
    const response = await fetchWithAuth('/legends', {
        method: 'POST',
        body: JSON.stringify(data),
    });
    return response.json();
}

/**
 * 获取传奇作品详情
 * @param {string} id - 传奇ID
 */
export async function getLegend(id) {
    const response = await fetchWithAuth(`/legends/${id}`);
    return response.json();
}

/**
 * 获取传奇作品列表
 * @param {Object} params - { sort, style, page, limit }
 */
export async function getLegends({ sort = 'hot', style = 'all', page = 1, limit = 12 } = {}) {
    const params = new URLSearchParams({ sort, style, page, limit });
    const response = await fetchWithAuth(`/legends?${params}`);
    return response.json();
}

/**
 * 更新传奇作品
 * @param {string} id - 传奇ID
 * @param {Object} data - { title, description }
 */
export async function updateLegend(id, data) {
    const response = await fetchWithAuth(`/legends/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    });
    return response.json();
}

/**
 * 删除传奇作品
 * @param {string} id - 传奇ID
 */
export async function deleteLegend(id) {
    const response = await fetchWithAuth(`/legends/${id}`, {
        method: 'DELETE',
    });
    return response.json();
}

/**
 * 获取我的传奇作品
 * @param {Object} params - { page, limit }
 */
export async function getMyLegends({ page = 1, limit = 20 } = {}) {
    const params = new URLSearchParams({ page, limit });
    const response = await fetchWithAuth(`/legends/my?${params}`);
    return response.json();
}

// =============================================
// 致敬系统 API (Salutes)
// =============================================

/**
 * 致敬作品
 * @param {string} legendId - 传奇ID
 */
export async function saluteLegend(legendId) {
    const response = await fetchWithAuth(`/legends/${legendId}/salute`, {
        method: 'POST',
    });
    return response.json();
}

/**
 * 取消致敬
 * @param {string} legendId - 传奇ID
 */
export async function unsaluteLegend(legendId) {
    const response = await fetchWithAuth(`/legends/${legendId}/salute`, {
        method: 'DELETE',
    });
    return response.json();
}

/**
 * 获取致敬者列表
 * @param {string} legendId - 传奇ID
 * @param {Object} params - { page, limit }
 */
export async function getSaluters(legendId, { page = 1, limit = 20 } = {}) {
    const params = new URLSearchParams({ page, limit });
    const response = await fetchWithAuth(`/legends/${legendId}/saluters?${params}`);
    return response.json();
}

// =============================================
// 骑士档案 API (Riders)
// =============================================

/**
 * 获取骑士档案
 * @param {string} riderId - 骑士ID
 */
export async function getRiderProfile(riderId) {
    const response = await fetchWithAuth(`/riders/${riderId}`);
    return response.json();
}

/**
 * 更新我的档案
 * @param {Object} data - { bio, signature }
 */
export async function updateMyProfile(data) {
    const response = await fetchWithAuth('/riders/me', {
        method: 'PATCH',
        body: JSON.stringify(data),
    });
    return response.json();
}

/**
 * 获取我的荣耀数据
 */
export async function getMyGlory() {
    const response = await fetchWithAuth('/riders/me/glory');
    return response.json();
}

/**
 * 关注骑士
 * @param {string} riderId - 骑士ID
 */
export async function followRider(riderId) {
    const response = await fetchWithAuth(`/riders/${riderId}/follow`, {
        method: 'POST',
    });
    return response.json();
}

/**
 * 取消关注
 * @param {string} riderId - 骑士ID
 */
export async function unfollowRider(riderId) {
    const response = await fetchWithAuth(`/riders/${riderId}/follow`, {
        method: 'DELETE',
    });
    return response.json();
}

/**
 * 获取骑士的传奇作品
 * @param {string} riderId - 骑士ID
 * @param {Object} params - { page, limit }
 */
export async function getRiderLegends(riderId, { page = 1, limit = 20 } = {}) {
    const params = new URLSearchParams({ page, limit });
    const response = await fetchWithAuth(`/riders/${riderId}/legends?${params}`);
    return response.json();
}

// =============================================
// 名人堂 API (Hall of Fame)
// =============================================

/**
 * 获取名人堂数据
 */
export async function getHallOfFame() {
    const response = await fetchWithAuth('/hall-of-fame');
    return response.json();
}

/**
 * 获取排行榜
 * @param {Object} params - { type: 'legends'|'riders', period: 'week'|'month'|'all' }
 */
export async function getLeaderboard({ type = 'legends', period = 'week' } = {}) {
    const params = new URLSearchParams({ type, period });
    const response = await fetchWithAuth(`/leaderboard?${params}`);
    return response.json();
}

/**
 * 获取编辑精选
 * @param {Object} params - { page, limit }
 */
export async function getFeaturedLegends({ page = 1, limit = 10 } = {}) {
    const params = new URLSearchParams({ page, limit });
    const response = await fetchWithAuth(`/legends/featured?${params}`);
    return response.json();
}

// =============================================
// 分享 API (Share)
// =============================================

/**
 * 生成分享资源
 * @param {string} legendId - 传奇ID
 * @param {string} channel - 分享渠道
 */
export async function generateShareAssets(legendId, channel = 'poster') {
    const response = await fetchWithAuth(`/legends/${legendId}/share`, {
        method: 'POST',
        body: JSON.stringify({ channel }),
    });
    return response.json();
}

/**
 * 记录分享
 * @param {string} legendId - 传奇ID
 * @param {string} channel - 分享渠道
 */
export async function trackShare(legendId, channel) {
    const response = await fetchWithAuth(`/legends/${legendId}/share/track`, {
        method: 'POST',
        body: JSON.stringify({ channel }),
    });
    return response.json();
}

// =============================================
// 常量定义
// =============================================

/**
 * 骑士等级配置
 */
export const RIDER_LEVELS = [
    { level: 1, title: '新手骑士', titleEn: 'Novice Rider', icon: '🔰', minSalutes: 0 },
    { level: 2, title: '铁骑士', titleEn: 'Iron Rider', icon: '🛡️', minSalutes: 50 },
    { level: 3, title: '钢骑士', titleEn: 'Steel Rider', icon: '⚔️', minSalutes: 200 },
    { level: 4, title: '银骑士', titleEn: 'Silver Rider', icon: '🥈', minSalutes: 500 },
    { level: 5, title: '金骑士', titleEn: 'Golden Rider', icon: '🥇', minSalutes: 1000 },
    { level: 6, title: '白金骑士', titleEn: 'Platinum Rider', icon: '💎', minSalutes: 2000 },
    { level: 7, title: '大师骑士', titleEn: 'Master Rider', icon: '👑', minSalutes: 5000 },
    { level: 8, title: '传奇骑士', titleEn: 'Legend Rider', icon: '🔱', minSalutes: 10000 },
    { level: 9, title: '神话骑士', titleEn: 'Mythic Rider', icon: '⚡', minSalutes: 25000 },
    { level: 10, title: '不朽骑士', titleEn: 'Immortal Rider', icon: '🌟', minSalutes: 50000 },
];

/**
 * 徽章定义
 */
export const BADGES = {
    // 创作类
    first_creator: { id: 'first_creator', name: '首创者', nameEn: 'Pioneer', icon: '🎨', category: 'creation' },
    viral_maker: { id: 'viral_maker', name: '爆款制造机', nameEn: 'Viral Maker', icon: '🔥', category: 'creation' },
    photo_master: { id: 'photo_master', name: '摄影大师', nameEn: 'Photo Master', icon: '📸', category: 'creation' },
    story_teller: { id: 'story_teller', name: '故事大师', nameEn: 'Story Teller', icon: '✍️', category: 'creation' },
    trend_setter: { id: 'trend_setter', name: '精准审美', nameEn: 'Trend Setter', icon: '🎯', category: 'creation' },
    // 社交类
    social_star: { id: 'social_star', name: '社交达人', nameEn: 'Social Star', icon: '🤝', category: 'social' },
    helpful_rider: { id: 'helpful_rider', name: '热心骑士', nameEn: 'Helpful Rider', icon: '💬', category: 'social' },
    generous_saluter: { id: 'generous_saluter', name: '慷慨致敬者', nameEn: 'Generous Saluter', icon: '🎁', category: 'social' },
    influencer: { id: 'influencer', name: '意见领袖', nameEn: 'Influencer', icon: '🌟', category: 'social' },
    // 荣誉类
    champion: { id: 'champion', name: '冠军骑士', nameEn: 'Champion', icon: '🏆', category: 'honor' },
    editors_pick: { id: 'editors_pick', name: '编辑之选', nameEn: "Editor's Pick", icon: '📌', category: 'honor' },
    hall_of_fame: { id: 'hall_of_fame', name: '名人堂成员', nameEn: 'Hall of Famer', icon: '🏛️', category: 'honor' },
    verified: { id: 'verified', name: '官方认证', nameEn: 'Verified', icon: '👔', category: 'honor' },
};

/**
 * 里程碑定义
 */
export const MILESTONES = [
    { type: 'salutes', value: 10, celebration: 'toast' },
    { type: 'salutes', value: 100, celebration: 'modal' },
    { type: 'salutes', value: 1000, celebration: 'fireworks' },
    { type: 'salutes', value: 10000, celebration: 'legendary' },
    { type: 'views', value: 100, celebration: 'toast' },
    { type: 'views', value: 1000, celebration: 'modal' },
    { type: 'views', value: 10000, celebration: 'fireworks' },
    { type: 'featured', value: 1, celebration: 'special' },
    { type: 'hall_of_fame', value: 1, celebration: 'legendary' },
];

/**
 * 获取骑士等级信息
 * @param {number} level - 等级数字
 * @param {string} lang - 语言
 */
export function getRiderLevelInfo(level, lang = 'zh') {
    const levelInfo = RIDER_LEVELS.find(l => l.level === level) || RIDER_LEVELS[0];
    return {
        ...levelInfo,
        title: lang === 'zh' ? levelInfo.title : levelInfo.titleEn,
    };
}

/**
 * 获取徽章信息
 * @param {string} badgeId - 徽章ID
 * @param {string} lang - 语言
 */
export function getBadgeInfo(badgeId, lang = 'zh') {
    const badge = BADGES[badgeId];
    if (!badge) return null;
    return {
        ...badge,
        name: lang === 'zh' ? badge.name : badge.nameEn,
    };
}

/**
 * 格式化数字（K/M 格式）
 * @param {number} num - 数字
 */
export function formatNumber(num) {
    if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
}

/**
 * 生成唯一编号
 * @param {number} id - 数字ID
 */
export function formatSerialNumber(id) {
    return `#${String(id).padStart(7, '0')}`;
}
